"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Play, Sparkles, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { SNIPPET_CAPTION_CLASSES } from "@/lib/landing/snippet-caption";

/**
 * Sechs echte Ausschnitte aus dem laufenden Tool, als Slideshow statt Kartenstapel
 * (Erik 2026-08-14: die vorherige ueberlappende Kaskade zeigte jede Karte nur
 * teilweise - als Slideshow mit genau einer sichtbaren Karte koennen die Snippets
 * deutlich groesser dargestellt werden UND Besucher sehen jedes Snippet vollstaendig
 * statt angeschnitten). Autoplay alle 8s (Landingpage-Feedback 2026-08-17, Punkt 5:
 * "bitte aller 8 sek anderes Snipping einblenden", vorher 5s), Pfeile + Bubble-Dots
 * fuer manuelle Navigation - der Intervall-Timer haengt bewusst von `index` ab, damit
 * jede manuelle Navigation die naechsten 8s neu anstossen, statt dass Autoplay eine
 * gerade erst angeklickte Karte sofort wieder wegschaltet.
 *
 * Sieben Slides (Landingpage-Feedback 2026-08-17, Runde 3 Punkt 2): je ein Snippet pro
 * Hero-Bullet (`app/page.tsx` -> `HERO_BULLETS`), gleiche Reihenfolge/Keywords - erst die
 * Pro-Features, dann die zwei Max-exklusiven (Dividenden-Tracking, Premium-Aktienanalysen).
 * "Aktienkennzahlen" (zwischenzeitlich entfernt, als die Slideshow auf 6 Slides gekuerzt
 * wurde) ist jetzt als 2. Slide zurueck - alle Bild-Dateien via scripts/capture-screenshot.mjs
 * + Nachbearbeitung aus einem eigens angelegten Musterdepot erzeugt (siehe backlog.md/
 * STATUS.md fuer Details), kein Live-Rendering zur Laufzeit.
 *
 * `tier` steuert die neue "ab Pro"/"ab Max"-Banderole oben rechts (Punkt 18) - Farben/
 * Icons spiegeln bewusst 1:1 die Preis-Kacheln (`pricing-bundle.tsx`): "pro" = Navy +
 * Sparkles (wie die Pro-Kachel selbst navy ist), "max" = Gold + Crown (wie die
 * Max-Kachel selbst gold ist).
 *
 * Fixes Seitenverhaeltnis (2:1) fuer ALLE Slides statt pro Bild individueller Groesse
 * (Erik: Slides sollen "immer die gleiche Groesse" haben UND "enden ca. wo die
 * Bulletpoints enden"). object-CONTAIN statt -cover, damit nichts Inhaltliches
 * abgeschnitten wird. Kein Crossfade-Zwischenzustand mit reduzierter Deckkraft (Erik:
 * nicht "milchig") - inaktive Slides sind hart auf opacity-0 statt auf einen sichtbaren
 * Zwischenwert.
 */
const AUTOPLAY_MS = 8000;

const SNIPPETS = [
  {
    label: "Erikonomie-Score",
    image: "/assets/landing/snippet-score.png",
    tier: "pro",
  },
  {
    label: "Aktienkennzahlen",
    image: "/assets/landing/snippet-kennzahlen.png",
    tier: "pro",
  },
  {
    label: "Depot-Röntgenblick",
    image: "/assets/landing/snippet-depot.png",
    tier: "pro",
  },
  {
    label: "Automatisierte Quartalsberichte",
    image: "/assets/landing/snippet-quartalsberichte.png",
    tier: "pro",
  },
  {
    label: "Meine News",
    image: "/assets/landing/snippet-news.png",
    tier: "pro",
  },
  {
    label: "Dividenden-Tracking",
    image: "/assets/landing/snippet-dividende.png",
    tier: "max",
  },
  {
    label: "Premium-Aktienanalysen",
    image: "/assets/landing/snippet-analysen.png",
    tier: "max",
  },
] as const;

function TierBadge({ tier }: { tier: "pro" | "max" }) {
  const isMax = tier === "max";
  return (
    <span
      className={cn(
        "absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold shadow-sm",
        isMax ? "bg-gold text-ink" : "bg-primary text-primary-foreground",
      )}
    >
      {isMax ? (
        <Crown className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      ) : (
        <Sparkles className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
      )}
      ab {isMax ? "Max" : "Pro"}
    </span>
  );
}

export function FeatureSnippets() {
  const [index, setIndex] = useState(0);
  const active = SNIPPETS[index];

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % SNIPPETS.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [index]);

  const prev = () => setIndex((i) => (i - 1 + SNIPPETS.length) % SNIPPETS.length);
  const next = () => setIndex((i) => (i + 1) % SNIPPETS.length);

  return (
    <div
      className="reveal relative w-full lg:flex lg:h-full lg:flex-col"
      role="region"
      aria-roledescription="Slideshow"
      aria-label="Erikonomie Funktionsvorschau"
    >
      {/* Caption WIEDER vor dem Rahmen (Landingpage-Feedback Runde 5, 2026-08-18, Punkt 1) -
          loest die Runde-4-Umstellung (Rahmen vor Caption) explizit ab: die Caption unter dem
          Bild wirkte zu nah an der Suchleiste/zu "kopflos" oben am Bild. Der erste Bulletpoint
          soll trotzdem weiterhin auf Hoehe der BILD-Oberkante liegen, nicht der Caption (Runde
          4, Punkt 4, weiterhin gueltig) - dafuer bekommt die Bullet-Spalte in app/page.tsx
          jetzt einen unsichtbaren Spacer in exakt Caption-Hoehe (HERO_BULLET_CAPTION_SPACER_CLASSES,
          gleiche Font-Klassen wie hier, damit beide garantiert exakt gleich hoch sind statt
          eines geschaetzten Pixelwerts). */}
      <p key={active.label} className={cn("reveal mb-3 shrink-0 text-center text-gold-text", SNIPPET_CAPTION_CLASSES)}>
        {active.label}
      </p>
      <div className="relative aspect-[2/1] w-full overflow-hidden rounded-2xl bg-card shadow-[0_20px_38px_-16px_rgba(22,38,92,0.4)] ring-1 ring-black/[0.05] lg:aspect-auto lg:min-h-0 lg:flex-1">
        <TierBadge tier={active.tier} />
        {SNIPPETS.map((snippet, i) => (
          <div
            key={snippet.label}
            className={cn(
              "absolute inset-0 transition-opacity duration-500 ease-out",
              i === index ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
            aria-hidden={i !== index}
          >
            <Image
              src={snippet.image}
              alt={snippet.label}
              fill
              sizes="(min-width: 1024px) 700px, 100vw"
              priority={i === 0}
              className="object-contain"
            />
          </div>
        ))}

        {/* Pfeile ohne Kreis-Hintergrund, nur die reinen Navy-Dreiecke (Landingpage-Feedback
            2026-08-17, Punkt 7: "ohne den weißen Kreis darum... nur entsprechend die navy
            blauen Dreiecke die, wenn man rüberhovert sich hervorheben") - `Play`-Icon
            (gefuellt, kein Chevron-Strich) statt Kreis-Chip, Hover hebt Farbe (Navy -> Gold)
            + leichte Vergroesserung hervor statt eines Hintergrund-Chips. Jetzt INNERHALB
            des Bildrahmens statt Geschwister davon (Feedback-Runde 2026-08-17 #2), damit
            `top-1/2` sich weiterhin auf die Bildmitte bezieht, nicht mehr auf die Mitte der
            gesamten Karte inkl. Caption/Dots - relevant seit der Rahmen ab `lg` variabel
            hoch ist statt immer aspect-[2/1]. Default-Deckkraft 45% (Landingpage-Feedback
            Runde 3, 2026-08-17, Punkt 12: "ein wenig unauffälliger") - vorher permanent
            volle Navy-Deckkraft, was neben den Snippet-Inhalten selbst zu praesent wirkte;
            Hover hebt auf volle Deckkraft UND Gold an, bleibt dadurch weiterhin klar
            auffindbar/bedienbar. */}
        <button
          type="button"
          onClick={prev}
          aria-label="Vorheriges Snippet"
          className="group absolute left-1 top-1/2 z-20 -translate-y-1/2 p-2 sm:left-2"
        >
          <Play
            fill="currentColor"
            className="h-6 w-6 rotate-180 text-primary opacity-45 transition-all duration-150 group-hover:scale-125 group-hover:text-gold-text group-hover:opacity-100 sm:h-7 sm:w-7"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Nächstes Snippet"
          className="group absolute right-1 top-1/2 z-20 -translate-y-1/2 p-2 sm:right-2"
        >
          <Play
            fill="currentColor"
            className="h-6 w-6 text-primary opacity-45 transition-all duration-150 group-hover:scale-125 group-hover:text-gold-text group-hover:opacity-100 sm:h-7 sm:w-7"
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="mt-4 flex shrink-0 items-center justify-center gap-2">
        {SNIPPETS.map((snippet, i) => (
          <button
            key={snippet.label}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`${snippet.label} anzeigen`}
            aria-current={i === index}
            className={cn(
              "h-2 rounded-full transition-all",
              i === index ? "w-6 bg-primary" : "w-2 bg-border hover:bg-muted-foreground/40",
            )}
          />
        ))}
      </div>
    </div>
  );
}
